import { IMainNavigationItem, ISubMenuItem } from '@models/IHeader';
import SubNavTab from '@organisms/Header/subNav/SubNavTab';
import updateDataLayer from '@utils/helpers/gtm';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import {
  Dispatch,
  FocusEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

interface Props {
  readonly mainNavigation: IMainNavigationItem[];
  readonly activeIndex: number;
  readonly isOpenDesktop: boolean;
  readonly setIsOpenDesktop: Dispatch<SetStateAction<boolean>>;
}

const DesktopSubNav = ({
  isOpenDesktop,
  setIsOpenDesktop,
  mainNavigation,
  activeIndex,
}: Props) => {
  const desktopSubNavRef = useRef<HTMLDivElement>(null);
  const [activeSubNav, setActiveSubNav] = useState<ISubMenuItem>();
  const [secondLevelActiveIndex, setSecondLevelActiveIndex] = useState(0);
  const [activeMenu, setActiveMenu] = useState<ISubMenuItem[]>([]);
  const isInIFrame = useIsInIFrame();

  const mainCategory = mainNavigation[activeIndex]?.title || '';

  const handleLevelTwoClick = (item: string) => {
    updateDataLayer({
      event: 'menuUsed',
      content_type: 'header_menu_level_2',
      main_category: mainCategory,
      sub_category: item,
      tertiary_category: undefined,
    });
  };

  const handleBlurOnLastSubNavItem = (event: FocusEvent<HTMLLIElement>) => {
    if (!desktopSubNavRef?.current?.contains(event.relatedTarget as Node))
      setIsOpenDesktop(false);
  };

  useEffect(() => {
    if (activeMenu && activeMenu[0]) {
      const { navigationCards, links, title } = activeMenu[0] || {};
      setActiveSubNav({ navigationCards, links, title });
    }
  }, [activeMenu]);

  useEffect(() => {
    if (activeIndex >= 0) {
      const menu = mainNavigation[activeIndex]!.subMenuItems;
      setActiveMenu(menu);
    }
  }, [activeIndex, mainNavigation]);

  return (
    <div
      className={classNames(
        { hidden: !isOpenDesktop },
        'desktop-header absolute left-0 right-0 top-[100%] z-100'
      )}
      ref={desktopSubNavRef}
    >
      <motion.div
        onHoverEnd={() => setIsOpenDesktop(false)}
        key="content"
        initial="collapsed"
        animate="open"
        exit="collapsed"
        variants={{
          open: { opacity: 1 },
          collapsed: { opacity: 0 },
        }}
        className="h-auto bg-grey-200 outline-none"
      >
        <motion.ul className="top flex flex-row bg-white pb-7 pl-32 pr-32 pt-7">
          {activeMenu?.map(
            ({ link, navigationCards, links, title = '' }, index) => {
              const linkTitle = link?.name;
              const linkUrl = link?.url;
              const linkTarget = link?.target;
              const isLastItem = index === activeMenu.length - 1;

              return (
                <motion.li
                  key={`level-2-${linkTitle}-${title}`}
                  onHoverStart={() => {
                    setIsOpenDesktop(true);
                    setActiveSubNav({ navigationCards, links });
                    setSecondLevelActiveIndex(index);
                  }}
                  onClick={() => {
                    setIsOpenDesktop(true);
                    setActiveSubNav({ navigationCards, links });
                    setSecondLevelActiveIndex(index);
                  }}
                  onKeyDown={() => {
                    setIsOpenDesktop(true);
                    setActiveSubNav({ navigationCards, links });
                    setSecondLevelActiveIndex(index);
                  }}
                  onBlur={isLastItem ? handleBlurOnLastSubNavItem : undefined}
                  className={classNames(
                    'body-two cursor-pointer pr-6',
                    secondLevelActiveIndex === index
                      ? 'text-black-100'
                      : 'text-grey-500'
                  )}
                >
                  {linkUrl ? (
                    <a
                      href={linkUrl}
                      target={isInIFrame ? '_parent' : linkTarget}
                      onClick={() => handleLevelTwoClick(title)}
                      data-testid={`CT-01-Header-Second-Level-Nav-Category-Link-${title}`}
                    >
                      <div
                        className="second-level-nav-title body-two whitespace-nowrap"
                        data-testid={`CT-01-Header-Second-Level-Nav-Category-Title-${title}`}
                      >
                        {title}
                      </div>
                    </a>
                  ) : (
                    <span
                      className="second-level-nav-title body-two whitespace-nowrap"
                      data-testid={`CT-01-Header-Second-Level-Nav-Category-Title-${title}`}
                    >
                      {title}
                    </span>
                  )}
                  {secondLevelActiveIndex === index ? (
                    <div className="mx-auto h-2 w-2 rounded-full bg-yellow" />
                  ) : null}
                  {secondLevelActiveIndex === index &&
                    activeSubNav &&
                    (!!activeSubNav.navigationCards?.length ||
                      !!activeSubNav.links?.length) && (
                      <SubNavTab
                        subCategory={title}
                        activeTab={activeSubNav}
                        mainCategory={mainCategory}
                        dataTestId={`CT-01-Header-Sub-Nav-Tab-${title}`}
                      />
                    )}
                </motion.li>
              );
            }
          )}
        </motion.ul>
      </motion.div>
    </div>
  );
};

export default DesktopSubNav;
